import React, { useRef, useEffect, useState } from "react";
import {
  I2cButton,
  I2cSwitch,
  I2cAvatar,
  I2cFileUpload,
  I2cIcon,
  I2cInput,
  I2cPhoneField, I2cRadioGroup, I2cRadio, I2cListItem
} from "@webcomponents/i2cwebcomponents/dist/react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import ErrorMessage from "../../../Components/SharedComponent/ErrorMessage/ErrorMessage";
import { useMutation } from "react-query";
import AlertMessage from "../../../Components/SharedComponent/Alert/Alert";
import "./UserForm.scss";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { validationSchema, updateValidationSchema } from "./validationSchema";
import { useQuery } from "react-query";
import UserManagmentService from "../../../Services/UsermanagmentService";
import { OverlayLoader } from "../../../Components/Loader/Loader";

/**
 * UserForm component for adding or updating a user.
 * @returns {JSX.Element} UserForm component.
 */
const UserForm = () => {
  const navigate = useNavigate();
  const form = useRef();
  const Manual = useRef(null);
  const { id: userId } = useParams();
  const [profileImg, setProfileImg] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const isUserError = searchParams.get("user-error") ?? false;
  const isUserUpdateError = searchParams.get("update-user-error") ?? false;
  // User creation mutation
  const userCreation = useMutation({
    mutationFn: async (values) => await UserManagmentService.createUser(values),
    onSuccess: (data) => {
      navigate("/users?user-added=true");
    },
    onError: (data) => {
      navigate("/users/add?user-error=true");
    },
  });

  const handleAvatarClick = () => {
    Manual.current.shadowRoot
      .querySelector(".dropzone.file-layout-row.dz-clickable.dz--mobile")
      .click();
  };
  // User creation mutation
  const userUpdation = useMutation({
    mutationFn: async (values) => {
      return await UserManagmentService.updateUser(userId, values);
    },
    onSuccess: (data) => {
      navigate("/users?user-updated=true");
    },
    onError: (data) => {
      searchParams.set("update-user-error", true);
      setSearchParams(searchParams);
    },
  });

  // Fetching user by id query
  const {
    data: user,
    isLoading,
    error,
    refetch,
  } = useQuery(
    ["user", userId],
    () => UserManagmentService.getSingleUser(userId),
    {
      enabled: !!userId, // Only fetch if userId is present
      staleTime: Infinity,
    },
  );
  const handleButtonClick = () => {
    navigate(".", { replace: true });
  };

  useEffect(() => {
    if (isUserUpdateError) {
      setTimeout(() => {
        handleButtonClick();
      }, 5000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserUpdateError]);
  useEffect(() => {
    if (userId) {
      // Refetch when userId changes
      refetch();
    }
  }, [userId, refetch]);

  const userCreationError = userCreation.error;
  const userUpdateError = userUpdation.error;
  // const getCheckedClass = (value) => {
  //   return value === methods.getValues("role_id")
  //     ? "update-role-single-field-checked"
  //     : "";
  // };
  const superAdmin = user?.data?.user.role_nice_name === "super_admin";
  /**
   * Handle user creation or updation.
   * @param {object} e - Form event object.
   */
  const handleUserCreation = (e) => {
    if (!userId) {
      userCreation.mutate(e);
    }
    else {
      const updatedUser = { ...e, is_unlocked: methods.watch("is_unlocked") };
      userUpdation.mutate(updatedUser);

    }
  };

  // Methods declaration
  const methods = useForm({
    resolver: zodResolver(!userId ? validationSchema : updateValidationSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    shouldUseNativeValidation: false,
    criteriaMode: "firstError",
    defaultValues: {
      name: "",
      email: "",
      password: "Temp/12345678",
      password_confirmation: "Temp/12345678",
      designation: "",
      contact: "",
      country_code: "",
      company: "i2c", 
      role_id: 4,
      is_active: false,
      is_unlocked: true,
      avatar: "",
    },
  });

  /**
   * Handle role change.
   * @param {object} e - Checkbox change event.
   * @param {number} valueToSet - Role ID to set.
   */
  // const handleRoleChange = (e, valueToSet) => {
  //   const isChecked = e.target.checked;
  //   if (isChecked) {
  //     methods.setValue("role_id", valueToSet);
  //     methods.clearErrors("role_id");
  //   } else {
  //     if (methods.getValues("role_id") === valueToSet) {
  //       methods.setValue("role_id", null);
  //       methods.setError("role_id", { message: "Role is Required" });
  //     }
  //   }
  // };
  const handleRoleChange = (e) => {
    const valueToSet = parseInt(e.target.value, 10);
    methods.setValue("role_id", valueToSet);
    methods.clearErrors("role_id");
  };
  
  // Hook form method
  const {
    handleSubmit,
    formState: { errors },
  } = methods;

  useEffect(() => {
    if (user && userId) {
      methods.setValue("company", user?.data?.user?.company);
      methods.setValue("contact", user.data.user.contact);
      methods.setValue("country_code", user?.data?.user?.country_code ?? "");
      methods.setValue("designation", user.data.user.designation);
      methods.setValue("email", user.data.user.email);
      methods.setValue("name", user.data.user.name);
      methods.setValue("password", user.data.user.password);
      methods.setValue(
        "avatar",
        `data:image/jpeg;base64,${user.data.user.avatar}`,
      );
      methods.setValue(
        "password_confirmation",
        user.data.user.password_confirmation,
      );
      methods.setValue("role_id", user.data.user.role_id);
      methods.setValue(
        "is_active",
        user.data.user.is_active === 1 ? true : false,
      );
  
      methods.setValue(
        "is_unlocked", user.data.user.is_unlocked);
      // if (Manual.current.getDropZone()?.files?.length > 0) {
      //   setProfileImg(
      //     Manual.current.getDropZone().files[
      //       Manual.current.getDropZone().files.length - 1
      //     ].dataURL,
      //   );
      //   methods.setValue(
      //     "avatar",
      //     Manual.current.getDropZone().files[
      //       Manual.current.getDropZone().files.length - 1
      //     ].dataURL,
      //   );
      // }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, userId]);

  if (error) {
    return (
      <div className="layout_aside_logged_user users-listing">
        <div i2c-grid="6@md offset-4@md" className="no-user-container">
          <I2cIcon
            className="cs-icon-shadowed"
            variant="shadowed"
            name="linear-info-circle"
          />
          <div className="no-user-title">User Not Found</div>
          <div className="no-user-text">
            It seems like the user don't exist.
          </div>
        </div>
      </div>
    );
  }

  /**
   * Render UserForm component.
   * @returns {JSX.Element} UserForm component.
   */
  return (
    <React.Fragment>
      <div className="layout_aside_logged_user user-form-container">
        <div onClick={() => navigate("/users")} className="go-back-button">
          <I2cIcon
            name="cs-linear-chevron-left"
            label="icon"
            library="default"
            className="go-back-button-icon"
          />
          Back
        </div>
        <div className="user-form-header">
          <h2>{userId ? "Update User" : "Add New User"}</h2>
        </div>
        <div className="user-form">
          <div>
            {isUserError && (
              <>
                <div style={{ marginTop: "var(--i2c-spacing-6)" }}></div>
                <div onClick={handleButtonClick}>
                  <AlertMessage
                    duration={5000}
                    open={isUserError}
                    message={
                      userCreationError?.response?.errors?.email ??
                      userCreationError?.response?.message
                    }
                  // userCreationError?.response?.message
                  />
                </div>
                <div style={{ marginBottom: "var(--i2c-spacing-6)" }}></div>
              </>
            )}
            {isUserUpdateError && (
              <>
                <div style={{ marginTop: "var(--i2c-spacing-6)" }}></div>
                <div onClick={handleButtonClick}>
                  <AlertMessage
                    duration={5000}
                    open={isUserUpdateError}
                    message={
                      userUpdateError?.response?.errors[
                      Object.keys(userUpdateError?.response?.errors)[0]
                      ] ?? userUpdateError?.response?.message
                    }
                  // userCreationError?.response?.message
                  />
                </div>
                <div style={{ marginBottom: "var(--i2c-spacing-6)" }}></div>
              </>
            )}
            <form
              onSubmit={handleSubmit(handleUserCreation)}
              ref={form}
              autoComplete="on"
            >
              <div className="user-form-user-avatar">
                <Controller
                  name="avatar"
                  control={methods.control}
                  render={({ field }) => (
                    <>
                      <I2cFileUpload
                        {...field}
                        auto-upload
                        loader-style="circular"
                        icon={null}
                        label=""
                        ref={Manual}
                        multiple={false}
                        caption=""
                        message=""
                        no-fp-item-upload-btn
                        onI2cUploadComplete={(e) => {
                          field.onChange(e);
                          if (Manual.current.getDropZone()?.files?.length > 0) {
                            setProfileImg(
                              Manual.current.getDropZone().files[
                                Manual.current.getDropZone().files.length - 1
                              ].dataURL,
                            );
                            methods.setValue(
                              "avatar",
                              Manual.current.getDropZone().files[
                                Manual.current.getDropZone().files.length - 1
                              ].dataURL,
                            );
                          }
                        }}
                      ></I2cFileUpload>
                      <I2cAvatar
                        className="table-avatar"
                        image={profileImg || methods.watch("avatar")}
                        name="User Avatar"
                        info="Click on the image to update avatar."
                        label="User Avatar"
                        size="2xl"
                        onClick={handleAvatarClick}
                      ></I2cAvatar>
                    </>
                  )}
                />
              </div>
              <div className="user-form-fields-container">
                <div className="form-single-field-wrapper">
                  <Controller
                    name="name"
                    control={methods.control}
                    render={({ field }) => (
                      <I2cInput
                        size="large"
                        label="Name"
                        placeholder="Enter name"
                        name="name"
                        required
                        value={methods.watch("name")}
                        className={`auth__custom-input fw-400`}
                        invalid={errors.name ? true : false}
                        onI2cInput={field.onChange}
                        onI2cBlur={field.onBlur}
                      ></I2cInput>
                    )}
                  />
                  {/* <ErrorMessage
                    show={errors.name ? true : false}
                    message={errors.name?.message ?? "Invalid input"}
                  /> */}
                </div>
                <div className="form-single-field-wrapper">
                  <Controller
                    name="email"
                    control={methods.control}
                    render={({ field }) => (
                      <I2cInput
                        size="large"
                        label="Email"
                        placeholder="Enter User Email"
                        name="email"
                        required
                        type="email"
                        value={methods.watch("email")}
                        disabled={!!userId}
                        className={`auth__custom-input fw-400`}
                        invalid={errors.email ? true : false}
                        onI2cInput={field.onChange}
                        onI2cBlur={field.onBlur}
                      ></I2cInput>
                    )}
                  />
                  {/* <ErrorMessage
                    show={errors.email ? true : false}
                    message={errors.email?.message ?? "Invalid input"}
                  /> */}
                </div>
                {!userId && (
                  <div className="form-single-field-wrapper">
                    <Controller
                      name="password"
                      control={methods.control}
                      render={({ field }) => (
                        <I2cInput
                          size="large"
                          type="password"
                          label="Password"
                          value={methods.watch("password")}
                          placeholder="Enter Password"
                          name="password"
                          required
                          invalid={errors.password ? true : false}
                          className="login__custom-input fw-400"
                          onI2cInput={(e) => {
                            field.onChange(e);
                            methods.trigger();
                          }}
                          onI2cBlur={field.onBlur}
                          togglePassword={true /* passIcon */}
                        >
                          <I2cIcon
                            slot="hide-password-icon"
                            name={"linear-eye"}
                          />
                          <I2cIcon
                            slot="show-password-icon"
                            name={"linear-eye-slash"}
                          />
                        </I2cInput>
                      )}
                    />
                    <ErrorMessage
                      show={
                        methods.watch("password").length > 0 &&
                        (errors.password ? true : false)
                      }
                      message={errors.password?.message ?? "Invalid input"}
                    />
                  </div>
                )}
                {!userId && (
                  <div className="form-single-field-wrapper">
                    <Controller
                      name="password_confirmation"
                      control={methods.control}
                      render={({ field }) => (
                        <I2cInput
                          size="large"
                          type="password"
                          label="Confirm Password"
                          placeholder="Enter Password"
                          required
                          value={methods.watch("password_confirmation")}
                          name="password_confirmation"
                          invalid={errors.password_confirmation ? true : false}
                          className="login__custom-input fw-400"
                          onI2cInput={(e) => {
                            if (
                              e.target.value !== methods.getValues("password")
                            ) {
                              methods.setError("password_confirmation", {
                                message: "Passwords donot match",
                              });
                            }
                            field.onChange(e);
                            methods.trigger();
                          }}
                          onI2cBlur={field.onBlur}
                          togglePassword={true /* passIcon */}
                        >
                          <I2cIcon
                            slot="hide-password-icon"
                            name={"linear-eye"}
                          />
                          <I2cIcon
                            slot="show-password-icon"
                            name={"linear-eye-slash"}
                          />
                        </I2cInput>
                      )}
                    />
                    <ErrorMessage
                      show={
                        methods.watch("password_confirmation").length > 0 &&
                        (errors.password_confirmation ? true : false)
                      }
                      message={
                        errors.password_confirmation?.message ?? "Invalid input"
                      }
                    />
                  </div>
                )}
                <div className="form-single-field-wrapper">
                  <Controller
                    name="designation"
                    control={methods.control}
                    render={({ field }) => (
                      <I2cInput
                        size="large"
                        label="Designation"
                        placeholder="Enter Designation"
                        required
                        value={methods.watch("designation")}
                        name="designation"
                        className={`auth__custom-input fw-400`}
                        invalid={errors.designation ? true : false}
                        onI2cInput={field.onChange}
                        onI2cBlur={field.onBlur}
                      ></I2cInput>
                    )}
                  />
                  {/* <ErrorMessage
                    show={errors.designation ? true : false}
                    message={errors.designation?.message ?? "Invalid input"}
                  /> */}
                </div>
                <div className="form-single-field-wrapper">
                  <I2cPhoneField
                      format="INTERNATIONAL"
                    label="Contact Number"
                    tooltip-help="Contact Number"
                    name="contact"
                    size="large"
                    country-box
                    show-flags
                    required
                    className="auth__custom-input fw-400"
                    selectedCountry={methods.watch("country_code") || "US"}
                    value={methods.watch("contact")}
                    // value="+9230000000"
                    errorMessageVisible={false}
                    onI2cChange={(e) => {
                      const country = e.target.country;
                      const isValid = e.target.isValid;
                      const value = e.target.value;
                      methods.setValue("country_code", country);
                      methods.setValue("contact", value);
                      if (!isValid) {
                        methods.setError("contact", {
                          message: e.target.guide,
                        });
                      } else {
                        methods.clearErrors("contact");
                      }
                    }}
                  />
                </div>
                <div className="form-single-field-wrapper">
                  <Controller
                    name="company"
                    control={methods.control}
                    render={({ field }) => (
                      <I2cInput
                        size="large"
                        label="Company Name"
                        placeholder="Enter Company Name"
                        name="company"
                        required
                        value={methods.watch("company")}
                        className={`auth__custom-input fw-400`}
                        invalid={errors.company ? true : false}
                        onI2cInput={field.onChange}
                        onI2cBlur={field.onBlur}
                      ></I2cInput>
                    )}
                  />
                  {/* <ErrorMessage
                    show={errors.company ? true : false}
                    message={errors.company?.message ?? "Invalid input"}
                  /> */}
                </div>
              
              </div>
            
              <div className="update-role-container">
                <div className="update-role-text">Update Role</div>
                <I2cRadioGroup onI2cChange={handleRoleChange}>
                  <div className="update-role-fields-container"> 
                    {superAdmin && (
                      <I2cListItem
                        variant="receipt"
                        className="option-container"
                        label="Super Admin"
                        checked={methods.getValues("role_id") === 1}
                        type="checkbox"
                      >
                        <I2cRadio
                          filled
                          slot="checkbox"
                          value="1"
                          checked={methods.getValues("role_id") === 1}
                        ></I2cRadio>

                      </I2cListItem>
                    )}
                    <I2cListItem 
                      variant="receipt"
                      className="option-container"
                      label="Admin"
                      checked={methods.getValues("role_id") === 2}
                      type="checkbox"
                    >
                      <I2cRadio
                         filled
                        slot="checkbox"
                        value="2"
                        checked={methods.getValues("role_id") === 2}
                      ></I2cRadio>

                    </I2cListItem>
                    <I2cListItem 
                      variant="receipt"
                      label="Manager"
                      className="option-container"
                      checked={methods.getValues("role_id") === 4}
                      type="checkbox"
                    >
                      <I2cRadio
                        filled
                        slot="checkbox"
                        value="4"
                        checked={methods.getValues("role_id") === 4}
                      ></I2cRadio>
                    </I2cListItem> 
                    <I2cListItem 
                      variant="receipt"
                      className="option-container"
                      label="Sales"
                      checked={methods.getValues("role_id") === 3}
                      type="checkbox"
                    >
                      <I2cRadio
                       filled
                       slot="checkbox"
                        value="3"
                        checked={methods.getValues("role_id") === 3}
                      ></I2cRadio>

                    </I2cListItem>  
                    <I2cListItem 
                      variant="receipt"
                      className="option-container"
                      label=" Client"
                      checked={methods.getValues("role_id") === 5}
                      type="checkbox"
                    >
                      <I2cRadio
                       filled
                        slot="checkbox"
                        value="5"
                        checked={methods.getValues("role_id") === 5}
                      ></I2cRadio>
                    </I2cListItem> 
                    <I2cListItem 
                      variant="receipt"
                      label="Viewonly"
                      className="option-container"
                      checked={methods.getValues("role_id") === 6}
                      type="checkbox"
                    >
                      <I2cRadio
                       filled
                        slot="checkbox"
                        value="6"
                        checked={methods.getValues("role_id") === 6}
                      ></I2cRadio>

                    </I2cListItem>
                     {userId && (
                      <I2cListItem 
                        variant="receipt"
                        label="Stakeholder"
                        className="option-container"
                        checked={methods.getValues("role_id") === 7}
                        type="checkbox"
                      >
                        <I2cRadio
                         filled
                          slot="checkbox"
                          value="7"
                          checked={methods.getValues("role_id") === 7}
                        ></I2cRadio>
                      </I2cListItem>
                    )}
                  </div>
                </I2cRadioGroup>
                <ErrorMessage
                  show={errors.role_id ? true : false}
                  message={errors.role_id?.message ?? "Invalid input"}
                />
              </div>

              <div className="user-status-container">
                <div className="user-status-text">User Status</div>
                <div className="switchFlex">
                  <div className="user-status-field">
                    <Controller
                      name="is_active"
                      control={methods.control}
                      render={({ field }) => (
                        <I2cSwitch
                          className="user-status-field-checkbox"
                          size="sm"
                          theme
                          name="is_active"
                          checked={methods.watch("is_active")}
                          onI2cChange={(e) => field.onChange(e.target.checked)}
                        >
                          Active
                        </I2cSwitch>
                      )}
                    />
                  </div>
                  {userId ?
                    <div className="user-status-field">

                      <Controller
                        name="is_unlocked"
                        control={methods.control}
                        render={({ field }) => (
                          <I2cSwitch
                            className="user-status-field-checkbox"
                            size="sm"
                            theme
                            name=""
                            checked={methods.watch("is_unlocked")}
                            onI2cChange={(e) => field.onChange(e.target.checked)}
                          >
                            Unlock User Profile
                          </I2cSwitch>
                        )}
                      />
                    </div> : ""
                  }
                </div>
              </div>
              <div className="form-footer">
                <I2cButton
                  className="form-footer-cancel-button form-footer-common-button"
                  variant="secondary gray"
                  onClick={() => navigate("/users")}
                  loading={userCreation.isLoading || userUpdation.isLoading}
                  disabled={userCreation.isLoading || userUpdation.isLoading}
                >
                  Cancel
                </I2cButton>
                <I2cButton
                  className="form-footer-submit-button form-footer-common-button"
                  variant="primary"
                  type="submit"
                  loading={userCreation.isLoading || userUpdation.isLoading}
                  disabled={
                    Object.keys(errors).length ||
                    userCreation.isLoading ||
                    userUpdation.isLoading
                  }
                >
                  {userId ? "Update User" : "Add User"}
                </I2cButton>
              </div>
            </form>
          </div>
        </div>
        {(isLoading || userCreation.isLoading || userUpdation.isLoading) && (
          <OverlayLoader />
        )}
      </div>
    </React.Fragment>
  );
};

export default UserForm;
